import { useCallback, useRef, useState } from 'react'
import { apiGet } from '../../../../../generic/Api_Functions'
import { jobDetailPartBlockUrl } from '../utils/jobDetailApi'

// Historia 169 — el estado de las filas desplegables de la huella (D6a/D6b, RN28, CU2-CU3, CU6),
// FUERA de `useJobDetail` (03-dominio §"Modelo de presentación (Jupiter)"): no toca ni el reloj de 5 s
// ni las invariantes de generación de la #160. Cada fila se pide SOLO al desplegarse (RN28); plegarla
// descarta su contenido; volver a desplegarla la pide de nuevo. Un token por (job, pieza, bloque)
// descarta las respuestas en vuelo cuando cambia la pieza, el job o se pliega antes de que lleguen
// (S74/S76). Nunca programa un refresco propio (RN28: los detalles no entran en el tick de 5 s).
//
// UX-10 (2026-09-26, decisión del PO): las filas INTENTOS (D6c) y CONTEXTO (D6d) se retiraron de la
// huella — solo quedan MATERIALS/INSPECTIONS; Jupiter no vuelve a pedir nunca D6c ni D6d.

export const FOOTPRINT_BLOCK = { MATERIALS: 'materials', INSPECTIONS: 'inspections' }
export const FOOTPRINT_DETAIL_STATUS = { IDLE: 'IDLE', LOADING: 'LOADING', READY: 'READY', FAILED: 'FAILED' }

const EMPTY_BLOCK = { status: FOOTPRINT_DETAIL_STATUS.IDLE, data: {}, errorKey: '' }
const EMPTY_BLOCKS = {
  [FOOTPRINT_BLOCK.MATERIALS]: EMPTY_BLOCK,
  [FOOTPRINT_BLOCK.INSPECTIONS]: EMPTY_BLOCK
}
const EMPTY_EXPANDED = {}

const errorLiteralOf = (error) => (error && typeof error.message === 'string' ? error.message : '')

/** @returns {{ expanded: Object, blocks: Object, expand: Function, collapse: Function, retry: Function }} */
const usePartFootprintDetail = (fabricationOrderId, jobReferenceId, partId) => {
  const [expanded, setExpanded] = useState(EMPTY_EXPANDED)
  const [blocks, setBlocks] = useState(EMPTY_BLOCKS)

  const tokensRef = useRef({})
  // O-J1: secuencia MONÓTONA compartida por los bloques (MATERIALS/INSPECTIONS, UX-10: ya no son
  // cuatro), nunca por bloque y nunca reiniciada a 0 — si el reset de pieza/job vaciara el mapa de
  // tokens, el primer `expand` del bloque en la pieza NUEVA volvería a valer 1, el mismo token que ya
  // llevaba una petición en vuelo de la pieza ANTERIOR sobre ese mismo bloque, y la guarda
  // `tokensRef.current[block] !== token` dejaría de distinguirlas.
  const tokenSeqRef = useRef(0)
  const nextToken = () => { tokenSeqRef.current += 1; return tokenSeqRef.current }
  const paramsRef = useRef({})
  paramsRef.current = { fabricationOrderId, jobReferenceId, partId }

  // Cambiar de pieza o de job pliega las filas (MATERIALS/INSPECTIONS) y descarta lo que estuviera en
  // vuelo (S76, CU6).
  // Patrón "ajustar el estado durante el render" (React): comparar con la clave anterior y, si cambió,
  // pedir el reinicio ANTES de pintar, sin esperar a un efecto (que llegaría un render tarde).
  const key = fabricationOrderId + ':' + jobReferenceId + ':' + partId
  const [resetKey, setResetKey] = useState(key)
  if (resetKey !== key) {
    setResetKey(key)
    setExpanded(EMPTY_EXPANDED)
    setBlocks(EMPTY_BLOCKS)
    // Sube el token de TODOS los bloques (MATERIALS/INSPECTIONS) con la misma secuencia monótona
    // (nunca los vacía ni los reinicia a 0): invalida cualquier petición en vuelo de la pieza/job
    // anteriores YA aunque el usuario no vuelva a desplegar nada, y dado que `expand` toma el
    // SIGUIENTE de la misma secuencia, el primer `expand` de la pieza nueva nunca puede coincidir con
    // un token ya emitido (O-J1).
    Object.values(FOOTPRINT_BLOCK).forEach((block) => { tokensRef.current[block] = nextToken() })
  }

  const load = useCallback((block) => {
    const { fabricationOrderId: foId, jobReferenceId: jobId, partId: pId } = paramsRef.current
    tokensRef.current[block] = nextToken()
    const token = tokensRef.current[block]
    setBlocks((current) => ({ ...current, [block]: { status: FOOTPRINT_DETAIL_STATUS.LOADING, data: {}, errorKey: '' } }))
    apiGet(jobDetailPartBlockUrl(foId, jobId, pId, block))
      .then((data) => {
        if (tokensRef.current[block] !== token) return
        setBlocks((current) => ({ ...current, [block]: { status: FOOTPRINT_DETAIL_STATUS.READY, data: data || {}, errorKey: '' } }))
      })
      .catch((error) => {
        if (tokensRef.current[block] !== token) return
        setBlocks((current) => ({
          ...current,
          [block]: { status: FOOTPRINT_DETAIL_STATUS.FAILED, data: {}, errorKey: errorLiteralOf(error) || 'This block could not be loaded' }
        }))
      })
  }, [])

  const expand = useCallback((block) => {
    setExpanded((current) => ({ ...current, [block]: true }))
    load(block)
  }, [load])

  const collapse = useCallback((block) => {
    // Sube el token: una respuesta que llegue después de plegar no pinta nada (RN28).
    tokensRef.current[block] = nextToken()
    setExpanded((current) => ({ ...current, [block]: false }))
    setBlocks((current) => ({ ...current, [block]: EMPTY_BLOCK }))
  }, [])

  const retry = useCallback((block) => load(block), [load])

  return { expanded, blocks, expand, collapse, retry }
}

export default usePartFootprintDetail
